/**
 * ParticlesManager.js
 * Implements the layer where the particles will appear.
 */

import { CoreEvents, Game, GameScene, GameSprite, GameTween } from "pixi_core";

import { Common } from "../Common";
import { EnemyParticle } from "./EnemyParticle";
import { EnemyAttackParticle } from "./EnemyAttackParticle";
import { EnemyAbilityParticle } from "./EnemyAbilityParticle";

import {
    GameEvents,
    BubbleColor,
    SceneLayers,
    Tutorials,
    TutorialStep,
    BUBBLE_TINTS,
    LAST_ATTACK_PARTICLE_ACKNOWLEDGEMENT_DELAY,
    NB_ATTACK_PARTICLES_SPRITES,
    FIRST_LEVEL
} from "../../Constants";

export class ParticlesManager {
    /**
     * @public
     * @param {GameScene} scene
     */
    constructor(scene) {
        Common.particles = this;

        /** @private @type {GameScene} */
        this._scene = scene;

        /** @private @type {Boolean} */
        this._waitForParticlesCreation = false;

        /** @private @type {Boolean} */
        this._allDamageParticlesCreated = false;

        /** @private @type {GameSprite[]} */
        this._particles = [];

        /** @private @type {EnemyParticle[]} */
        this._enemyParticles = [];

        /** @private @type {GameTween[]} */
        this._particleTweens = [];

        /** @private @type {Number} */
        this._currentTurnTime = 0;
        /** @private @type {Number} */
        this._lastParticleTime = 0;

        Game.on(CoreEvents.eGAME_RESUMED, this._onGameResumed, this);

        this.beginTurn();
    }

    /**
     * A new turn begins
     * @public
     */
    beginTurn() {
        this._allDamageParticlesCreated = false;
        this._waitForParticlesCreation = false;

        this._currentTurnTime = 0;
        this._lastParticleTime = 0;
    }

    /**
     * @public
     * @param {Number} deltatime
     */
    updateLoop(deltatime) {
        this._currentTurnTime += deltatime;

        if (
            !this._waitForParticlesCreation &&
            !this._allDamageParticlesCreated &&
            this._currentTurnTime > this._lastParticleTime + LAST_ATTACK_PARTICLE_ACKNOWLEDGEMENT_DELAY
        ) {
            this._allDamageParticlesCreated = true;
        }
    }

    /**
     * @public
     * @param {Object} position
     * @param {BubbleColor} color
     */
    createDamageParticle(position, color) {
        this._waitForParticlesCreation = false;

        this._lastParticleTime = this._currentTurnTime;

        for (const enemy of Common.enemies.currentEnemies) {
            if (enemy && enemy.isAlive && enemy.weakness.length > 0) {
                let particle4 = this._createSubParticle(position.x, position.y, color);
                let particle3 = this._createSubParticle(position.x, position.y, color);
                let particle2 = this._createSubParticle(position.x, position.y, color);
                let particle1 = this._createSubParticle(position.x, position.y, color);

                this._particles.push(particle1);
                this._particles.push(particle2);
                this._particles.push(particle3);
                this._particles.push(particle4);

                let controlPoint1 = {
                    x: position.x + ((enemy.x - position.x) * Math.random()) / 2,
                    y: enemy.y + Common.settings.particleCurveMultiplier1 * Math.random() * (Math.random() > 0.5 ? 1 : -1)
                };
                let controlPoint2 = {
                    x: position.x + (enemy.x - position.x) * Math.random(),
                    y: enemy.y + Common.settings.particleCurveMultiplier2 * Math.random() * (Math.random() > 0.5 ? 1 : -1)
                };

                // Particle rotation
                let totalRotation = Common.settings.particleRotation;
                if (Math.random() > 0.5) {
                    totalRotation *= -1;
                }

                let particle1Tween = new GameTween({ t: 0, angle: 0 })
                    .to({ t: 1, angle: totalRotation }, Common.settings.particleTravelingTime)
                    .onUpdate((motion) => {
                        this._bezier(motion.t, position, controlPoint1, controlPoint2, enemy.pos, particle1);
                        particle1.angle = motion.angle;
                    })
                    .easing(GameTween.Easing.QuadraticIn)
                    .onComplete(() => {
                        particle1.visible = false;

                        if (Common.currentLevel.getLevelNumber() === FIRST_LEVEL) {
                            Common.tutorial.openTutorial(Tutorials.eLVL_1, TutorialStep.eCLEAR_BUBBLES);
                        }

                        Game.fire(GameEvents.eDAMAGE_PARTICLE_ENEMY_HIT, enemy.index, color, 1);
                    });

                let particle2Tween = new GameTween({ t: 0, angle: 0 })
                    .to({ t: 1, angle: totalRotation }, Common.settings.particleTravelingTime - Common.settings.secondParticleDelay)
                    .onUpdate((motion) => {
                        this._bezier(motion.t, position, controlPoint1, controlPoint2, enemy.pos, particle2);
                        particle2.angle = motion.angle;
                    })
                    .easing(GameTween.Easing.QuadraticIn)
                    .onComplete(() => {
                        particle2.visible = false;
                    });

                let particle2DelayTween = new GameTween(null).to(null, Common.settings.secondParticleDelay).onComplete(() => {
                    particle2Tween.start();
                });

                let particle3Tween = new GameTween({ t: 0, angle: 0 })
                    .to({ t: 1, angle: totalRotation }, Common.settings.particleTravelingTime - Common.settings.thirdParticleDelay)
                    .onUpdate((motion) => {
                        this._bezier(motion.t, position, controlPoint1, controlPoint2, enemy.pos, particle3);
                        particle3.angle = motion.angle;
                    })
                    .easing(GameTween.Easing.QuadraticIn)
                    .onComplete(() => {
                        particle3.visible = false;
                    });

                let particle3DelayTween = new GameTween(null).to(null, Common.settings.thirdParticleDelay).onComplete(() => {
                    particle3Tween.start();
                });

                let particle4Tween = new GameTween({ t: 0, angle: 0 })
                    .to({ t: 1, angle: totalRotation }, Common.settings.particleTravelingTime - Common.settings.fourthParticleDelay)
                    .onUpdate((motion) => {
                        this._bezier(motion.t, position, controlPoint1, controlPoint2, enemy.pos, particle4);
                        particle4.angle = motion.angle;
                    })
                    .easing(GameTween.Easing.QuadraticIn)
                    .onComplete(() => {
                        particle4.visible = false;
                    });

                let particle4DelayTween = new GameTween(null).to(null, Common.settings.fourthParticleDelay).onComplete(() => {
                    particle4Tween.start();
                });

                this._particleTweens.push(particle1Tween);
                this._particleTweens.push(particle2Tween);
                this._particleTweens.push(particle3Tween);
                this._particleTweens.push(particle4Tween);
                this._particleTweens.push(particle2DelayTween);
                this._particleTweens.push(particle3DelayTween);
                this._particleTweens.push(particle4DelayTween);

                particle1Tween.start();
                particle2DelayTween.start();
                particle3DelayTween.start();
                particle4DelayTween.start();

                if (Game.isPaused("Tutorial")) {
                    particle1Tween.pause();
                    particle2DelayTween.pause();
                    particle3DelayTween.pause();
                    particle4DelayTween.pause();
                }
            }
        }
    }

    /**
     * @private
     * @param {Number} x
     * @param {Number} y
     * @param {BubbleColor} color
     */
    _createSubParticle(x, y, color) {
        const randomIndex = 1 + Math.floor(Math.random() * NB_ATTACK_PARTICLES_SPRITES);

        const particle = new GameSprite("IMG_AttackParticle0" + String(randomIndex), this._scene, SceneLayers.ePARTICLES);
        const particleTop = new GameSprite("IMG_AttackParticleTop0" + String(randomIndex));

        particle.addChild(particleTop);

        particle.x = x;
        particle.y = y;
        particle.scale.x = Common.settings.particleScale;
        particle.scale.y = Common.settings.particleScale;

        particle.tint = BUBBLE_TINTS[color];

        return particle;
    }

    /**
     * @public
     * @param {Number} startX
     * @param {Number} startY
     * @param {Number} targetX
     * @param {Number} targetY
     * @param {Function} callback
     */
    createEnemyAttackParticle(startX, startY, targetX, targetY, callback) {
        const particle = new EnemyAttackParticle(startX, startY, targetX, targetY, callback);
        this._scene.addChild(particle, SceneLayers.ePARTICLES);
        this._enemyParticles.push(particle);
    }

    /**
     * @public
     * @param {Number} startX
     * @param {Number} startY
     * @param {Number} targetX
     * @param {Number} targetY
     * @param {Function} callback
     */
    createEnemyAbilityParticle(startX, startY, targetX, targetY, callback) {
        const particle = new EnemyAbilityParticle(startX, startY, targetX, targetY, callback);
        this._scene.addChild(particle, SceneLayers.ePARTICLES);
        this._enemyParticles.push(particle);
    }

    /**
     * Bézier curve function for the particle trails
     * @private
     * @param {Number} t
     * @param {Object} p0
     * @param {Object} p1
     * @param {Object} p2
     * @param {Object} p3
     * @param {GameSprite} particle
     */
    _bezier(t, p0, p1, p2, p3, particle) {
        let cX = 3 * (p1.x - p0.x);
        let bX = 3 * (p2.x - p1.x) - cX;
        let aX = p3.x - p0.x - cX - bX;

        let cY = 3 * (p1.y - p0.y);
        let bY = 3 * (p2.y - p1.y) - cY;
        let aY = p3.y - p0.y - cY - bY;

        particle.x = aX * Math.pow(t, 3) + bX * Math.pow(t, 2) + cX * t + p0.x;
        particle.y = aY * Math.pow(t, 3) + bY * Math.pow(t, 2) + cY * t + p0.y;
    }

    /**
     * Absolutely wait for at least one particle to be created before considering completion
     * @public
     */
    waitForParticles() {
        this._waitForParticlesCreation = true;
    }

    /**
     * Are all particle animations completed?
     * @public
     * @returns {Boolean}
     */
    verifyCompletion() {
        if (!this._allDamageParticlesCreated) {
            return false;
        }

        for (const particleTween of this._particleTweens) {
            if (particleTween.isPlaying()) {
                return false;
            }
        }

        this._destroyParticlesAndTweens();
        return true;
    }

    /**
     * @private
     * @param {String} pauseName
     */
    _onGameResumed(pauseName) {
        if (pauseName === "Tutorial") {
            for (const particleTween of this._particleTweens) {
                particleTween.resume();
            }
        }
    }

    /**
     * @private
     */
    _destroyParticlesAndTweens() {
        for (const particle of this._particles) {
            particle.destroy();
        }
        this._particles = [];

        for (const particleTween of this._particleTweens) {
            particleTween.destroy();
        }
        this._particleTweens = [];

        for (const enemyParticle of this._enemyParticles) {
            enemyParticle.destroy();
        }
        this._enemyParticles = [];
    }

    /**
     * @public
     */
    destroy() {
        Common.particles = null;

        this._destroyParticlesAndTweens();
        Game.off(CoreEvents.eGAME_RESUMED, this._onGameResumed, this);
    }
}
